import { CURRENCY_LABEL, useBillingInfoGetter } from '@/helpers/payments';
import { SubscriptionType, type SubscriptionPlanDto } from '@editar-pdf/api';
import { CalendarX2, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface BillingSummaryProps {
  tariff: SubscriptionType;
  plans?: SubscriptionPlanDto[];
}

/**
 * What the card is actually charged, in words. The picker above quotes a weekly rate, so the
 * billed line and the renewal terms have to be here, and they are the fine print rather than
 * the headline. The two reassurances above them are the opposite: they answer the question
 * that stops people at a checkout, so they are lines of their own rather than a tail on a
 * paragraph nobody reads. Cancellation itself is support-only (§11, 2026-08-16): there is no
 * in-app cancel action by design.
 */
const BillingSummary = ({ tariff, plans }: BillingSummaryProps) => {
  const { t } = useTranslation('web', { keyPrefix: 'components.paymentModal' });
  const { t: tt } = useTranslation('web');
  const getBillingInfo = useBillingInfoGetter();

  const activePlan = plans?.find((v) => v.subscriptionType === tariff);

  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-2">
        <li className="flex items-center gap-2 text-ui text-ink">
          <CalendarX2 size={16} className="shrink-0 text-primary" />
          {t('trust.cancel')}
        </li>
        <li className="flex items-center gap-2 text-ui text-ink">
          <ShieldCheck size={16} className="shrink-0 text-primary" />
          {t('trust.refund')}
        </li>
      </ul>

      <p className="text-label text-pretty">
        {activePlan && (
          <>
            {t('billed')} {CURRENCY_LABEL}
            {activePlan.price}/{tt(`tariffs.period.${activePlan.subscriptionType}`)}.{' '}
          </>
        )}
        {getBillingInfo(tariff, plans)}
      </p>
    </div>
  );
};

export { BillingSummary };
